import React, { useRef } from 'react';
import classnames from 'classnames';
import { Building } from 'apolloClient/types';
import { getBuildingFloorplans } from 'src/utils/buildingUtils';
import { useHorizontalScrollFade } from 'hooks/useHorizontalScrollFade';
import { scrollOverlayToTop } from 'hooks/usePageScrollOverlay';
import ScrollFadeV2 from 'componentsV2/ScrollFadeV2';

interface BuildingFloorplanBodyV2Props {
  activeRowIndex: number | null;
  building: Building;
  openImage: (rowIndex: number) => void;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  onSelectRow?: (rowIndex: number) => void;
}

const columns = [
  {
    key: 'aptNo',
    heading: 'Apt#',
    headingMobile: 'Apt#',
  },
  {
    key: 'bedBath',
    heading: 'Bed/Baths',
  },
  {
    key: 'floors',
    heading: 'Floors',
  },
  {
    key: 'interiorExt',
    heading: 'Interior + Ext (sq ft)',
    headingDesktop: 'Int+Ext (sq ft)',
  },
];

const BuildingFloorplanBodyV2: React.FC<BuildingFloorplanBodyV2Props> = ({
  building,
  activeRowIndex,
  openImage,
  scrollRef,
  onSelectRow,
}) => {
  const { floorplans } = building;
  const floorplansData = getBuildingFloorplans(floorplans);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const { ref: horizontalScrollRef, showFade } =
    useHorizontalScrollFade<HTMLDivElement>(floorplansData.length);

  function handleRowClick(rowIndex: number) {
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (isDesktop && tableScrollRef.current) {
      tableScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (scrollRef?.current) {
      scrollOverlayToTop(scrollRef.current, 'smooth');
    }
    openImage(rowIndex);
    onSelectRow?.(rowIndex);
  }

  function handleRowKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    rowIndex: number
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleRowClick(rowIndex);
    }
  }

  function rowButtonProps(rowIndex: number) {
    return {
      role: 'button',
      tabIndex: 0,
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) =>
        handleRowKeyDown(event, rowIndex),
    };
  }

  function cellValue(value: any) {
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    return value;
  }

  function formatCellDisplay(columnKey: string, value: any) {
    const displayValue = cellValue(value);
    if (
      columnKey === 'aptNo' &&
      typeof displayValue === 'string' &&
      displayValue.includes(',')
    ) {
      return displayValue.replace(/,/g, ',\u200b');
    }
    return displayValue;
  }

  function renderColumnHeading(column: typeof columns[number]) {
    if (!('headingDesktop' in column)) return column.heading;
    return (
      <>
        <span className="lg:hidden">{column.heading}</span>
        <span className="hidden lg:inline">{column.headingDesktop}</span>
      </>
    );
  }

  return (
    <div
      ref={tableScrollRef}
      className="w-full pb-12 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:pb-0"
    >
      {/* Phone: columns keep their natural width and the table scrolls sideways to the screen edge */}
      <div className="relative -mr-5 md:mr-0">
        <div
          ref={horizontalScrollRef}
          className="overflow-x-auto no-scrollbar pr-5 md:pr-0 md:overflow-visible"
        >
          <div className="grid w-max min-w-full grid-cols-[repeat(4,auto)] lg:w-full lg:grid-cols-[minmax(0,1fr),repeat(3,minmax(100px,max-content))]">
            {columns.map((column, columnIndex) => (
              <div
                key={column.key}
                className={classnames(
                  'flex justify-center items-end px-[10px] pb-[5px] border-r border-b border-beige border-b-gray-darkBorder text-16-23-0.3 text-black bg-white font-sohneKraftig text-center whitespace-nowrap md:sticky md:top-0 md:z-10 no-liga',
                  { 'border-l': columnIndex === 0 }
                )}
              >
                {renderColumnHeading(column)}
              </div>
            ))}
            {floorplansData.length === 0 ? (
              <div className="col-span-full p-4 border border-t-0 border-beige text-center text-16-21-0.3">
                No floorplans available.
              </div>
            ) : (
              floorplansData.map((row, rowIndex) => {
                const isActive = rowIndex === activeRowIndex;
                return (
                  // display: contents lets the whole row share one hover state
                  <div key={rowIndex} className="contents group">
                    {columns.map((column, columnIndex) => (
                      <div
                        key={column.key}
                        // One tab stop per row; other cells stay plain text for screen readers
                        {...(columnIndex === 0 ? rowButtonProps(rowIndex) : {})}
                        onClick={() => handleRowClick(rowIndex)}
                        className={classnames(
                          'relative flex items-center justify-center p-[10px] border-r border-b border-beige text-16-23-0.3 font-sohneBuch text-center cursor-pointer no-liga hover-animation',
                          isActive
                            ? 'bg-gold-backgroundV2 text-black'
                            : 'bg-white text-black group-hover:bg-gray-accent group-hover:text-gray-darkBorder',
                          'whitespace-nowrap',
                          // Long apt lists wrap at commas in the fixed-width desktop column
                          columnIndex === 0 && 'border-l lg:whitespace-normal'
                        )}
                      >
                        {columnIndex === 0 && (
                          <span
                            className={classnames(
                              'absolute -left-px inset-y-0 w-[3px]',
                              isActive
                                ? 'bg-[#443700]'
                                : 'bg-transparent group-hover:bg-gold'
                            )}
                          />
                        )}
                        {formatCellDisplay(column.key, row[column.key])}
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <ScrollFadeV2 visible={showFade} className="md:hidden" />
      </div>
    </div>
  );
};

export default BuildingFloorplanBodyV2;
